import '@fs/react-scripts/polyfills'
import React, { useEffect, useState, Suspense, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { ApolloClient, ApolloProvider, InMemoryCache, HttpLink } from '@apollo/client'
import { appPath, mergeSentryConfig, sentryDSN } from '@fs/zion-config'
import { essentialLocales, setAppLocales, i18n } from '@fs/zion-locale'
import { useExperiment } from '@fs/zion-experiments'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { AppLayout } from '@fs/zion-ui'
import * as Sentry from '@sentry/browser'

import { NoticeLoading } from '@fs/zion-icon'
import { CommonUseProvider } from 'contexts'
import { getCookieHelper } from 'helpers'
import { LocalDataCacheProvider } from 'hooks'
import App from './App'
import './locales'
import * as serviceWorker from './serviceWorker'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: ['-extension:'], // Ignore browser extension errors
    })
  )
}

setAppLocales(essentialLocales)

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

function FrontierRoot() {
  const [rmsEndpointAuthEx] = useExperiment('rmsEndpointAuthEx')
  const [client, setClient] = useState()
  // Provide a way to toggle the footer
  const [disableFooter, setDisableFooter] = useState(() => false)
  const [disableHeader, setDisableHeader] = useState(false)
  const [disableSubheader, setDisableSubheader] = useState(false)
  // Provide a way to set the background color for the full page
  const [backgroundColor, setBackgroundColor] = useState(() => '')
  // Set to true to prevent vertical scrolling on the page (Per Matthew Larson, when true this helps keep the page full height)
  const [noScrollLayout, setNoScrollLayout] = useState(false)
  // Provide a way to use fullScreen mode of zion-root
  const [fullScreen, setFullScreen] = useState(() => false)
  const sessionId = useMemo(() => getCookieHelper('fssessionid'), [])

  useEffect(() => {
    function setApolloClient() {
      const httpLink = new HttpLink({
        uri: '/records/images/orchestration/',
        headers: {
          Authorization: `Bearer ${sessionId}`,
          'Timezone-Offset': new Date().getTimezoneOffset(),
          'User-Locale': i18n.language,
        },
      })

      setClient(
        new ApolloClient({
          cache: new InMemoryCache(),
          link: httpLink,
        })
      )
    }

    setApolloClient()
    i18n.on('languageChanged', setApolloClient)
  }, [sessionId])

  if (rmsEndpointAuthEx && client) {
    client.link.options.headers.rmsendpointauthex = 'true'
  }

  const rootProps = {
    analytics: true,
    footer: { hidden: disableFooter },
    fullScreen,
    header: { hidden: disableHeader },
    helpChatbot: false,
  }

  const appLayoutProps = {
    backgroundColor,
  }

  const defaultValues = {
    disableSubheader,
    setBackgroundColor,
    setDisableFooter,
    setDisableHeader,
    setDisableSubheader,
    setFullScreen,
    setNoScrollLayout,
  }

  return client ? (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <ApolloProvider client={client}>
          <Router basename={base}>
            <Root {...rootProps}>
              <AppLayout fullWidth noScroll={noScrollLayout} {...appLayoutProps}>
                <CommonUseProvider defaultValues={defaultValues}>
                  <LocalDataCacheProvider>
                    <App />
                  </LocalDataCacheProvider>
                </CommonUseProvider>
              </AppLayout>
            </Root>
          </Router>
        </ApolloProvider>
      </Suspense>
    </React.StrictMode>
  ) : null
}

ReactDOM.render(
  <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
    <FrontierRoot />
  </FrontierBaseProviders>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
